import { TenderType } from "./tender-type.enum";

/**
 * Represents a denomination item with its nominal value and quantity
 */
export class DenominationItem {
    /**
     * Gets or sets the denomination item ID
     */
    DenominationItemID?: number;

    /**
     * Gets or sets the item index
     */
    ItemIndex?: number;

    /**
     * Gets or sets the type record code
     */
    TypeRecord?: number;

    /**
     * Gets or sets the currency ISO code (e.g., "CZK")
     */
    CurrencyISOCode: string;

    /**
     * Gets or sets the denomination caption/name (e.g., "100", "200", "1000" for cash)
     */
    Caption: string;

    /**
     * Gets or sets the nominal/face value of the denomination
     */
    Value: number;

    /**
     * Gets or sets the quantity/count of this denomination
     */
    Quantity: number;

    /**
     * Gets the total value (Quantity * Value)
     */
    readonly Total?: number;

    /**
     * Gets or sets the previous quantity for comparison
     */
    PreviousQuantity?: number;

    /**
     * Gets or sets the change date
     */
    readonly ChangeDate?: Date;
}

export class CreateFinancialTransactionRequest {
        TransactionTypeCode: string;
        TotalPrice: number;
        CurrencyISOCode: string;
        ReasonText: string;
        ReasonCode: string;
        Note: string;
        AdditionalJsonData: string;
        FinancialTransactionType: FinancialTransactionType;
        TenderType: TenderType;
        TenderSubType: string;
        Attributes: { [key: string]: string };
        
        /**
         * Gets or sets the list of denomination items where user fills quantity for each denomination
         */
        DenominationItems: DenominationItem[];
    }

    export enum FinancialTransactionType {
        /**Deposit to safe from external source */
        DepositToSafe = 0,

        /**Deposit to till from external source */
        DepositToTill = 1,

        /**Move from safe to till */
        MoveFromSafeToTill = 2,

        /**Move from till to safe */
        MoveFromTillToSafe = 3,

        /**Pickup from safe for external purposes (eg. purchase of materials,...) */
        PickupFromSafe = 4,

        /**Pickup from till for external purposes (eg. purchase of materials,...) */
        PickupFromTill = 5,

        /**Quantification of deficiency */
        Deficit = 6,

        /**Quantification of excess */
        Excess = 7,

        /**Indication of safe initial value */
        SafeInitialValue = 8,

        /**Indication of safe final value */
        SafeFinalValue = 9,

        /**Indication of safe final value */
        AutomaticDepositTransferToNextClosure = 10,

        /**A special fiscal related transaction for opening of a day */
        DayOpening = 11,

        /**Automatic withdrawal of money before closure */
        AutomaticWithdrawalBeforeClosure = 12
    }


export enum TenderMovementReasonCode { 
    CASH_DEPOSIT = "1",
    CASH_WITHDRAWAL = "2",
    CASH_INVENTORY_CHECK = "3",
    PAPER_VALUABLES_CLOSING = "4",
    ELECTRONIC_VALUABLES_CLOSING = "5"
}

export enum TransactionTypeCodes {
    DOC_0_0 = "DOC_0_0",
    PickupFromTill = "PickupFromTill",
    PickupFromTillDocument = "PickupFromTillDocument",
    DepositToTill = "DepositToTill",
    CashInventoryCheck = "CashInventoryCheck",
    PaperValuablesClosing = "PaperValuablesClosing",
    ElectronicValuablesClosing = "ElectronicValuablesClosing"
}

/**
 * Request for CreateTransferTransaction API — creates a paired Source + Target financial transaction.
 */
export interface TransferTransactionRequest {
    Source: CreateFinancialTransactionRequest;
    Target: CreateFinancialTransactionRequest;
}

// ── Transfer transaction result models ────────────────────────────────────────

export interface TrxComponent {
    trxUID?: string;
    [key: string]: any;
}

export interface TrxRecord {
    name: string;
    header: { creationNo: number; isVoid: boolean };
    components: TrxComponent[];
}

export interface TrxContainer {
    records: TrxRecord[];
    properties: { [key: string]: any }[];
}

/**
 * Result returned by CreateTransferTransaction API.
 * Property names are lowercase to match the JSON response.
 */
export interface TransferTransactionResult {
    source: TrxContainer;
    target: TrxContainer;
}

/**
 * Extracts the Header-record trxUID from a TrxContainer.
 * Skips the zero-GUID used on child records.
 */
export function extractTrxUID(container: TrxContainer | null | undefined): string | null {
    if (!container) return null;
    const headerRecord = container.records?.find((r) => r.name === 'Header');
    if (!headerRecord) return null;
    return (
        headerRecord.components
            .map((c) => c['trxUID'] as string | undefined)
            .find((uid) => uid && uid !== '00000000-0000-0000-0000-000000000000') ?? null
    );
}
